import { useState, type KeyboardEvent } from 'react';
import { clearSession, getToken } from '../lib/api';

interface PairProps {
  pendingCode: string | null;
  busy: boolean;
  onCreate: (displayName: string) => void;
  onJoin: (code: string, displayName: string) => void;
}

/**
 * Pair / join screen — markup ported 1:1 from index.html, handlers from
 * main.ts's handleCreate/handleJoin. Kept as buttons + Enter-key handlers
 * rather than nested <form>s, exactly as the original DOM did.
 */
export function Pair({ pendingCode, busy, onCreate, onJoin }: PairProps) {
  const [displayName, setDisplayName] = useState('');
  const [code, setCode] = useState('');

  const create = () => {
    if (busy) return;
    onCreate(displayName.trim() || 'Anonymous');
  };

  const join = () => {
    if (busy) return;
    const normalized = code.trim().toUpperCase();
    if (normalized.length !== 6) {
      alert('Please enter a 6-character pair code.');
      return;
    }
    onJoin(normalized, displayName.trim() || 'Anonymous');
  };

  const onNameKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (code.length === 6 ? join : create)();
  };
  const onCodeKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') join();
  };

  // A member can land here with a half-dead stored session (token without a
  // couple, or members cached after a failed join) and then have no way out —
  // the Leave button only ever existed in chat. Keep one on the pair screen
  // too (card 0xhNPdS42FTh).
  const startFresh = () => {
    clearSession();
    window.location.reload();
  };

  return (
    <section className="pear-pair" id="pair-screen">
      <p className="pear-pair-subtitle">Two of you. One space to remember.</p>

      <div className="pear-pair-form">
        <div className="pear-pair-group">
          <input
            type="text"
            id="input-display-name"
            placeholder="Your name"
            className="pear-chat-input pear-input-center"
            aria-label="Display name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            onKeyDown={onNameKey}
            disabled={busy}
            autoComplete="name"
          />
          <button className="pear-btn pear-btn-mustard pear-btn-block" id="btn-create" disabled={busy} onClick={create}>
            Create a pair
          </button>
        </div>

        <div className="pear-or">or</div>

        <div className="pear-pair-group">
          <input
            type="text"
            id="input-join-code"
            placeholder="Pair code"
            className="pear-chat-input pear-input-center pear-input-code"
            aria-label="Pair code"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            onKeyDown={onCodeKey}
            disabled={busy}
            maxLength={6}
          />
          <button className="pear-btn pear-btn-blush pear-btn-block" id="btn-join" disabled={busy} onClick={join}>
            Join with code
          </button>
        </div>
      </div>

      <div className="pear-pair-exit">
        <button type="button" className="pear-btn pear-pair-exit-btn" id="btn-start-fresh" onClick={startFresh}>
          Start fresh
        </button>
        <span className="pear-pair-exit-note">
          {getToken()
            ? 'Clears this device’s saved session so you can pair from scratch.'
            : 'Clears anything saved on this device and reloads.'}
        </span>
      </div>

      <div className="pear-code-hint" aria-label="Pair code info">
        <span className="pear-code-label">Share this code with your partner</span>
        <code className="pear-code" id="pair-code">
          {pendingCode ?? '—'}
        </code>
      </div>
    </section>
  );
}
